import { useImperativeHandle, useState } from "react";

import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { Flex, isNullish, List, Text } from "@theorgchartdev/storybook";
import { enumFilterTypes, GetFilterType, ImportFilterModal } from "./ImportFilterModal";
import { handleListItemClick } from "@/Utils/ComponentLib";
import { GetLimitFields } from "./ImportFilterLimitFieldsInput";
import { GetWSMgr } from "@/Utils/StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { convertStdDateString } from "@coreapp/utils/convertStdDateString";
import FieldTypePill from "../../FieldsFormulasConfiguration/FieldTypePill";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { enumObjectTypes } from "@coreapp/constants";
import { IMPORT_FILTERS_TEST_PREFIX } from "../ImportFiltersUtils";

const SimpleLabel = ({ label }) => {
  return (
    <Flex flex={"1"}>
      <Text>{label}</Text>
    </Flex>
  );
};

const GetFilterInfo = (importFilter, chartConfig) => {
  const wsMgr = GetWSMgr();
  const orgInfo = wsMgr?.fOrgInfo;

  const theField = importFilter["field"] ?? appstr("notset");

  const labels = [];
  const filterType = GetFilterType(importFilter);
  const isPosition = IsPositionChart(chartConfig);

  switch (filterType) {
    case enumFilterTypes.seed: {
      labels.push(<SimpleLabel label={`${appstr("Field")}: ${theField}`} key="seed-field" />);

      if (isPosition) {
        labels.push(
          <Flex key="seed-field-type">
            <FieldTypePill
              type={importFilter.fieldType === enumObjectTypes.position ? "position" : "employee"}
              pillProps={{ className: "ocn-import-filters-field-type" }}
            />
          </Flex>
        );
      }

      labels.push(
        <SimpleLabel label={`${appstr("Value")}: ${importFilter["seed"]}`} key="seed-value" />
      );
      break;
    }
    case enumFilterTypes.limitfields: {
      const limitFields = GetLimitFields(importFilter, chartConfig);
      if (limitFields[enumObjectTypes.holder]?.length > 0) {
        if (isPosition)
          labels.push(
            <Flex key="holder-limit-fields-type">
              <FieldTypePill
                type={"employee"}
                pillProps={{ className: "ocn-import-filters-field-type" }}
              />
            </Flex>
          );
        labels.push(
          <SimpleLabel
            label={limitFields[enumObjectTypes.holder].join(", ")}
            key="holder-limit-fields"
          />
        );
      }

      if (limitFields[enumObjectTypes.position]?.length > 0) {
        if (isPosition)
          labels.push(
            <Flex key="position-limit-fields-type">
              <FieldTypePill
                type={"position"}
                pillProps={{ className: "ocn-import-filters-field-type" }}
              />
            </Flex>
          );
        labels.push(
          <SimpleLabel
            label={limitFields[enumObjectTypes.position].join(", ")}
            key="position-limit-fields"
          />
        );
      }
      break;
    }
    default: {
      const values = structuredClone(importFilter["values"] || []);
      const blankIdx = values.indexOf("");

      if (blankIdx > -1) {
        values.splice(blankIdx, 1, "(Blank)");
      }
      let filterValues = "";
      if (values.length > 0) {
        filterValues = values.join(", ");
      }
      if (
        !isNullish(importFilter.dateBracket?.range?.from) &&
        !isNullish(importFilter.dateBracket?.range?.to)
      ) {
        let fromDate = new Date(importFilter.dateBracket.range.from).toISOString();
        let toDate = new Date(importFilter.dateBracket.range.to).toISOString();

        const fromSplitResult = fromDate.split("T");
        let newFromDate = fromSplitResult[0].replace(/-/g, "");
        const fromDateObj = convertStdDateString(newFromDate);
        let fromLabel = fromDateObj.FormatDate(orgInfo.GetDateFormat());

        const toSplitResult = toDate.split("T");
        let newToDate = toSplitResult[0].replace(/-/g, "");
        const toDateObj = convertStdDateString(newToDate);
        let toLabel = toDateObj.FormatDate(orgInfo.GetDateFormat());

        filterValues = appstr("from") + ": " + fromLabel + " " + appstr("to") + ": " + toLabel;
        labels.push(
          <SimpleLabel label={`${appstr("Field")}: ${theField}`} key="date-field" />,
          <SimpleLabel label={filterValues} key="date-values" />
        );
      } else
        labels.push(
          <SimpleLabel label={`${appstr("Field")}: ${theField}`} key="field" />,
          <SimpleLabel label={`${appstr("Value")}s: ${filterValues}`} key="values" />
        );

      if (isPosition) {
        const fieldType =
          importFilter.fieldType === enumObjectTypes.position ? "position" : "employee";
        labels.splice(
          labels.length - 1,
          0,
          <Flex key="field-type">
            <FieldTypePill
              type={fieldType}
              pillProps={{ className: "ocn-import-filters-field-type" }}
            />
          </Flex>
        );
      }

      break;
    }
  }

  return [filterType, ...labels];
};

const BuildOptions = (importFilters, chartConfig, handlers) => {
  const options = [];
  importFilters.forEach((anImportFilter, i) => {
    const [filterType, ...labels] = GetFilterInfo(anImportFilter, chartConfig);

    options.push({
      clickHandler: handleListItemClick(() => undefined, handlers.onEdit),
      content: (
        <ActionListItem
          padding="large"
          contentBefore={
            <Flex gap="8px">
              <Flex flex={"1"}>
                <Text modifier="bold">{appstr(filterType)} </Text>
              </Flex>
              {labels}
            </Flex>
          }
          actions={[
            {
              iconName: "pen-to-square",
              tooltipProps: { message: appstr("Edit filter") },
              onClick: () => handlers.onEdit({ value: i }),
              dataTestId: `${IMPORT_FILTERS_TEST_PREFIX}-edit-filter-${i}`
            },
            {
              iconName: "trash-can",
              tooltipProps: { message: appstr("Delete filter") },
              iconProps: { color: "error" },
              onClick: () => handlers.onDelete({ value: i }),
              dataTestId: `${IMPORT_FILTERS_TEST_PREFIX}-delete-filter-${i}`
            }
          ]}
        />
      ),
      padding: false,
      value: i
    });
  });

  return options;
};

const NEW_SYMBOL = Symbol();

export const ImportFiltersSection = props => {
  const importFilters = props.chartConfig["import"]["importFilters"] || [];

  const [editFilterIdx, setEditFilterIdx] = useState(null);

  useImperativeHandle(props.handleRef, () => ({
    handleCreate
  }));

  const handleCreate = () => {
    setEditFilterIdx(NEW_SYMBOL);
  };

  const handleEdit = opt => {
    setEditFilterIdx(opt.value);
  };

  const handleCommit = nextImportFilters => {
    const nextChartConfig = {
      ...props.chartConfig,
      import: {
        ...props.chartConfig.import,
        importFilters: nextImportFilters
      }
    };

    props.handleUpdate(nextChartConfig, "import");
  };

  const handleDelete = opt => {
    const nextImportFilters = [
      ...importFilters.slice(0, opt.value),
      ...importFilters.slice(opt.value + 1)
    ];

    handleCommit(nextImportFilters);
  };

  const handleClose = () => {
    setEditFilterIdx(null);
  };

  const handleSave = nextFilter => {
    const nextImportFilters = [...importFilters];
    if (editFilterIdx === NEW_SYMBOL) {
      nextImportFilters.push(nextFilter);
    } else {
      nextImportFilters[editFilterIdx] = nextFilter;
    }

    handleCommit(nextImportFilters);
  };

  const options = BuildOptions(importFilters, props.chartConfig, {
    onEdit: handleEdit,
    onDelete: handleDelete
  });

  return (
    <>
      <Flex direction="column" gap="12px">
        {importFilters.length > 0 ? (
          <List listWidth="fill" optionSize={"large"} options={options} />
        ) : (
          <Text className="ocn-settings-text-tertiary">
            {appstr("No import filters have been added")}
          </Text>
        )}
      </Flex>
      {!isNullish(editFilterIdx) && (
        <ImportFilterModal
          isNew={editFilterIdx === NEW_SYMBOL}
          onClose={handleClose}
          onSave={handleSave}
          importFilters={importFilters}
          chartConfig={props.chartConfig}
          filter={importFilters[editFilterIdx]}
        />
      )}
    </>
  );
};
